import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { AavedakShell } from "@/components/layout/AavedakShell";
import { clearStoredSession, getStoredSession, setStoredSession } from "@/lib/authStore";
import { getAuthMe } from "@/lib/api";
import { AdminWorkspacePage } from "@/pages/AdminWorkspacePage";
import { ApplicantAuthPage } from "@/pages/ApplicantAuthPage";
import { ApplicantWorkspacePage } from "@/pages/ApplicantWorkspacePage";
import { BoothAuthPage } from "@/pages/BoothAuthPage";
import { BoothWorkspacePage } from "@/pages/BoothWorkspacePage";
import { HomeLandingPage } from "@/pages/HomeLandingPage";
import { IntakeWorkspacePage } from "@/pages/IntakeWorkspacePage";
import { RoleChangePasswordPage } from "@/pages/RoleChangePasswordPage";
import { RoleLoginPage } from "@/pages/RoleLoginPage";
import { TestUsersPage } from "@/pages/TestUsersPage";

const roleRouteMap = {
  admin: "/admin/dashboard",
  support: "/support/dashboard",
  org_admin: "/business/dashboard",
  booth_incharge: "/booth/dashboard",
  applicant: "/user/dashboard",
};

const ProtectedRoute = ({ session, roles, children, allowPasswordChange = false }) => {
  if (!session?.token) {
    return <Navigate to="/" replace />;
  }
  if (roles && !roles.includes(session.role)) {
    return <Navigate to={roleRouteMap[session.role] || "/"} replace />;
  }
  if (session.must_change_password && session.role !== "admin" && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }
  return children;
};

const AppRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(() => getStoredSession());
  const [language, setLanguage] = useState("hi");
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("aavedak-language");
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("aavedak-language", language);
  }, [language]);

  useEffect(() => {
    const validate = async () => {
      if (!session?.token) {
        setAuthLoaded(true);
        return;
      }
      try {
        const me = await getAuthMe(session.token);
        const updated = { ...session, ...me, must_change_password: me.must_change_password };
        setSession(updated);
        setStoredSession(updated);
      } catch {
        clearStoredSession();
        setSession(null);
      } finally {
        setAuthLoaded(true);
      }
    };
    validate();
  }, [session?.token]);

  useEffect(() => {
    if (!authLoaded) return;
    if (!session?.token) return;
    if (location.pathname === "/") return;
    if (session.must_change_password && location.pathname !== "/change-password") {
      navigate("/change-password", { replace: true });
    }
  }, [authLoaded, location.pathname, navigate, session]);

  const authActions = useMemo(
    () => ({
      onLogin: (payload) => {
        setSession(payload);
        setStoredSession(payload);
        const destination = payload.must_change_password ? "/change-password" : roleRouteMap[payload.role] || "/";
        navigate(destination, { replace: true });
      },
      onLogout: () => {
        clearStoredSession();
        setSession(null);
        toast.success("Logged out");
        navigate("/", { replace: true });
      },
      onPasswordChanged: () => {
        const updated = { ...(session || {}), must_change_password: false };
        setSession(updated);
        setStoredSession(updated);
        navigate(roleRouteMap[updated.role] || "/", { replace: true });
      },
    }),
    [navigate, session],
  );

  if (!authLoaded) {
    return (
      <div className="kiosk-bg flex min-h-screen items-center justify-center">
        <div className="rounded-2xl border bg-white px-8 py-6 text-xl font-semibold">Loading Aavedak...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<HomeLandingPage />} />
      <Route path="/test-users" element={<TestUsersPage />} />

      <Route path="/adminlogin" element={<RoleLoginPage role="admin" onLogin={authActions.onLogin} />} />
      <Route path="/supportlogin" element={<RoleLoginPage role="support" onLogin={authActions.onLogin} />} />
      <Route path="/businesslogin" element={<RoleLoginPage role="org_admin" onLogin={authActions.onLogin} />} />
      <Route path="/boothlogin" element={<RoleLoginPage role="booth_incharge" onLogin={authActions.onLogin} />} />
      <Route path="/login" element={<BoothAuthPage onLogin={authActions.onLogin} />} />
      <Route path="/user/login" element={<ApplicantAuthPage onLogin={authActions.onLogin} />} />

      <Route
        path="/change-password"
        element={
          <ProtectedRoute session={session} roles={["support", "org_admin", "booth_incharge"]} allowPasswordChange={true}>
            <RoleChangePasswordPage session={session} onPasswordChanged={authActions.onPasswordChanged} onLogout={authActions.onLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute session={session} roles={["admin"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <AdminWorkspacePage session={session} mode="admin" />
            </AavedakShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/dashboard"
        element={
          <ProtectedRoute session={session} roles={["support"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <AdminWorkspacePage session={session} mode="support" />
            </AavedakShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/dashboard"
        element={
          <ProtectedRoute session={session} roles={["org_admin"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <AdminWorkspacePage session={session} mode="org_admin" />
            </AavedakShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/booth/dashboard"
        element={
          <ProtectedRoute session={session} roles={["booth_incharge"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <BoothWorkspacePage session={session} language={language} />
            </AavedakShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/dashboard"
        element={
          <ProtectedRoute session={session} roles={["applicant"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <ApplicantWorkspacePage session={session} language={language} />
            </AavedakShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspace/intake"
        element={
          <ProtectedRoute session={session} roles={["applicant", "booth_incharge"]}>
            <AavedakShell session={session} language={language} onLanguageChange={setLanguage} onLogout={authActions.onLogout}>
              <IntakeWorkspacePage session={session} language={language} />
            </AavedakShell>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

export default App;
